
import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import FadeInWhenVisible from "./FadeInWhenVisible";
import { supabase } from "@/integrations/supabase/client";

interface Testimonial {
  id: string;
  rating: number;
  comment: string;
  userName: string;
  userTitle: string;
  agentName: string;
}

const TestimonialsSection: React.FC = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        // This query joins tables correctly using the foreign key relationships
        const { data, error } = await supabase
          .from('agent_feedback')
          .select(`
            id,
            rating,
            comment,
            user_id,
            agent_id,
            agents (
              name
            )
          `)
          .order('rating', { ascending: false })
          .limit(3);

        if (error) throw error;

        if (data) {
          // Get user names separately as a workaround for join issues
          const userIds = data.map(feedback => feedback.user_id).filter(Boolean);
          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, name')
            .in('id', userIds);

          if (profilesError) throw profilesError;

          // Create a map of user IDs to names
          const userNameMap = new Map();
          if (profilesData) {
            profilesData.forEach(profile => {
              userNameMap.set(profile.id, profile.name);
            });
          }

          const formattedTestimonials = data.map(feedback => ({
            id: feedback.id,
            rating: feedback.rating,
            comment: feedback.comment || "Great experience with this agent!",
            userName: userNameMap.get(feedback.user_id) || 'Anonymous User',
            userTitle: "User",
            agentName: feedback.agents?.name || "AI Agent"
          }));
          
          setTestimonials(formattedTestimonials);
        }
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  const renderStars = (rating: number) => {
    return Array(5).fill(0).map((_, index) => (
      <Star 
        key={index}
        className={`h-5 w-5 ${index < rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <section className="py-20 bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4">
        <FadeInWhenVisible>
          <div className="text-center mb-16">
            <Badge className="mb-2">Testimonials</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 dark:text-white">What Our Users Say</h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Hear from developers and users who have transformed their workflow with our AI agents
            </p>
          </div>
        </FadeInWhenVisible>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {loading ? (
            // Loading skeletons
            Array(3).fill(0).map((_, index) => (
              <Card key={index} className="border-none shadow-lg overflow-hidden animate-pulse">
                <CardContent className="p-8">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-4" />
                  <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded mb-6" />
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 mr-4" />
                    <div>
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2" />
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : testimonials.length > 0 ? (
            testimonials.map((testimonial, index) => (
              <FadeInWhenVisible key={testimonial.id} delay={index * 0.1}>
                <Card className="border-none shadow-lg overflow-hidden h-full">
                  <CardContent className="p-8">
                    <div className="flex items-center mb-4">
                      {renderStars(testimonial.rating)}
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-6 italic">
                      "{testimonial.comment}"
                    </p>
                    <div className="flex items-center">
                      <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden relative mr-4">
                        <img
                          src="/placeholder.svg"
                          alt={testimonial.userName}
                          className="object-cover w-full h-full"
                        />
                      </div>
                      <div>
                        <h4 className="font-semibold dark:text-white">{testimonial.userName}</h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Using {testimonial.agentName}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </FadeInWhenVisible>
            ))
          ) : (
            <div className="col-span-3 text-center py-10">
              <p className="text-gray-500">No testimonials available yet. Be the first to share your experience!</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
